import { Injectable, signal } from '@angular/core';
import {
  GetMenus,
  MenuModel,
  PagesModel,
  RoleAccessModel,
} from '@zp-shared/models/side-panel.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpRoutingService } from './http-routing.service';
import { API_ROUTES } from '@zp-shared/constants/api-routes.constants';
import { SnackbarService } from '@zen/ui/services/snackbar.service';
/**
 * Service used to access role and menus based on user.
 */
@Injectable({
  providedIn: 'root',
})
export class MenuService {
  /**
   * Signal to hold menus of the user.
   * @type { Signal<null | MenuModel[]> }
   */
  readonly menus = signal<null | MenuModel[]>(null);
  /**
   * Subject to check is menu fetched or not
   * @type { BehaviorSubject<boolean | null> }
   */
  private isMenuFetched = new BehaviorSubject<boolean | null>(null);
  /**
   * Observable to check is menu fetched or not
   * @type { Observable }
   */
  isMenuFetched$ = this.isMenuFetched.asObservable();
  /**
   * To import http client.
   * @param httpClient to access httpclient methods
   * @param snackbar to access snackbar methods
   */
  constructor(
    private httpService: HttpRoutingService,
    private snackbar: SnackbarService
  ) {}
  /**
   * Method is used to fetch roles for user.
   */
  fetchMenus(): void {
    if (this.isMenuFetched.value == null) {
      (
        this.httpService.getMethod(API_ROUTES.GET_MENUS) as Observable<GetMenus>
      ).subscribe({
        next: (res) => {
          this.menus.set(res.data);
          if(!res.data.length){
            this.openSnackBar('No menu permissions were found for this user.');
          }
          this.isMenuFetched.next(res.data.length ? true : false);
        },
        error: (e) => {
          this.openSnackBar(e.status == 401 ? 'Unauthorized' : 'Something went wrong')
          this.isMenuFetched.next(false);
        },
      });
    }
  }
  /**
   * Method used to get roles for the page.
   * @param url to get url of the page
   * @returns roleAccess
   */
  getRoleAccess(url: string): RoleAccessModel {
    let roleAccess: RoleAccessModel | undefined;
    for (const m of this.menus() || []) {
      if (m.parentMenuId == null && m.subMenus?.length == 0) {
        roleAccess = m.pages.find((p) => p.url == url)?.roleAccess;
        if (roleAccess) return roleAccess;
      }
      if (m.subMenus?.length) {
        for (const menu of m.subMenus || []) {
          roleAccess = menu.pages.find((item) => item.url === url)?.roleAccess;
          if (roleAccess) return roleAccess;
        }
      }
    }
    return { isRead: false, isWrite: false } as RoleAccessModel;
  }
  /**
   * Method used to show snackbar.
   * @param message to get error message
   */
  openSnackBar(message:string):void{
    this.snackbar.showSnackBar({
      actionType: 'error',
      message,
      durationTime: 2,
      buttonType: 'icon',
    });
  }
}
